import React, { useEffect } from "react";
import { Card, Grid, Stack, CircularProgress } from "@mui/material";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";

import {
  IoSparkles,
  IoRestaurant,
  IoSunny,
  IoMoon,
  IoLocationSharp,
  IoPerson,
  IoPeople,
  IoHome,
  IoBed,
  IoPaw,
  IoThermometer,
  IoCheckmarkCircle,
  IoStatsChart,
} from "react-icons/io5";
import { usePreferenceInsights } from "hooks/usePreferenceInsights";
import { getInsightColor } from "utils/preferenceClassifier";

import colors from "assets/theme/base/colors";
import rgba from "assets/theme/functions/rgba";

import useCommon from "hooks/useCommon";
import { getDashboardPreferenceInsights } from "store/slice/dashboardSlice";

const ICON_MAP = {
  IoSparkles,
  IoRestaurant,
  IoSunny,
  IoMoon,
  IoLocationSharp,
  IoPerson,
  IoPeople,
  IoHome,
  IoBed,
  IoPaw,
  IoThermometer,
  IoCheckmarkCircle,
  IoStatsChart,
};

const { dark, light, primary } = colors;

const getIcon = (name, options = {}) => {
  const { size = 14, color, context } = options;

  const Icon = ICON_MAP[name] || IoSparkles;

  const resolvedColor = color || (context ? getInsightColor(context) : primary.main);

  return <Icon color={resolvedColor} size={`${size}px`} />;
};

const renderGlassCard = ({ children, px = 2, py = 2, p }) => (
  <VuiBox
    px={p ?? px}
    py={p ?? py}
    borderRadius="18px"
    sx={{
      height: "100%",
      // background: "rgba(255,255,255,0.03)",
      border: `1px solid ${rgba(dark.main, 0.25)}`,
      backdropFilter: "blur(12px)",
    }}
  >
    {children}
  </VuiBox>
);

function GuestLifestyleInsights() {
  const { DISPATCH, STORE } = useCommon();

  const selectedHotelId = STORE((state) => state.ui.hotelId);

  const {
    preferenceInsights: insights,
    preferenceInsightsLoading,
    preferenceInsightsError,
  } = STORE((state) => state.dashboard);

  useEffect(() => {
    if (selectedHotelId) {
      DISPATCH(
        getDashboardPreferenceInsights({
          hotelId: selectedHotelId,
          questionnaireVersion: 1,
        })
      );
    }
  }, [DISPATCH, selectedHotelId]);

  {
    /* ================= LOADING STATE ================= */
  }

  if (preferenceInsightsLoading) {
    return (
      <Card>
        <VuiBox
          minHeight={260}
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          textAlign="center"
          px={3}
        >
          <CircularProgress size={26} color="primary" />
        </VuiBox>
      </Card>
    );
  }

  {
    /* ================= EMPTY STATE ================= */
  }

  if (preferenceInsightsError || !insights || !insights.insightCards?.length) {
    return (
      <Card>
        <VuiBox
          minHeight={280}
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          textAlign="center"
          px={3}
        >
          <VuiBox
            mb={2}
            sx={{
              height: 56,
              width: 56,
              borderRadius: "16px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "rgba(62,201,255,0.08)",
              border: "1px solid rgba(62,201,255,0.2)",
              backdropFilter: "blur(8px)",
            }}
          >
            <IoSparkles size={24} color={primary.main} />
          </VuiBox>

          <VuiTypography variant="lg" fontWeight="bold" sx={{ mb: 0.5 }}>
            {preferenceInsightsError ? "Unable to Load Insights" : "No Insights Yet"}
          </VuiTypography>

          <VuiTypography
            variant="caption"
            sx={{
              maxWidth: 320,
              opacity: 0.75,
              lineHeight: 1.5,
            }}
          >
            {preferenceInsightsError
              ? preferenceInsightsError || "Something went wrong while loading preference insights."
              : "We’ll start generating insights as soon as guests begin sharing their preferences. This helps you understand behavior and personalize their stay."}
          </VuiTypography>

          {!preferenceInsightsError && (
            <>
              <VuiBox
                mt={2}
                sx={{
                  width: 40,
                  height: 2,
                  borderRadius: 2,
                  background: "rgba(255,255,255,0.1)",
                }}
              />

              <VuiTypography variant="caption" sx={{ mt: 1.5, opacity: 0.6 }}>
                Data will appear here automatically
              </VuiTypography>
            </>
          )}
        </VuiBox>
      </Card>
    );
  }

  return (
    <Card>
      <VuiBox>
        <VuiBox mb={2.5}>
          <VuiTypography variant="lg" fontWeight="bold" sx={{ display: "block", lineHeight: 1.2 }}>
            {insights.title || "Guest Preference Insights"}
          </VuiTypography>

          <VuiTypography variant="caption" sx={{ display: "block", mt: 0.5 }}>
            {insights.subtitle || "Preferences captured through guest interaction flows"}
          </VuiTypography>
        </VuiBox>

        {/* ================= SUMMARY CARDS ================= */}
        {insights.summaryCards?.length > 0 && (
          <Grid container spacing={2} mb={2}>
            {insights.summaryCards.map((card, index) => (
              <Grid item xs={12} md={4} key={index}>
                {renderGlassCard({
                  px: 2,
                  py: 1.5,
                  children: (
                    <>
                      <VuiBox display="flex" alignItems="center" gap={1} mb={0.8}>
                        {getIcon(card.icon, { color: primary.main, size: 16 })}

                        <VuiTypography variant="caption">{card.title}</VuiTypography>
                      </VuiBox>

                      <VuiTypography variant="h5" fontWeight="medium">
                        {card.value}
                      </VuiTypography>

                      <VuiTypography color={"success"} variant="caption">
                        {card.sub}
                      </VuiTypography>
                    </>
                  ),
                })}
              </Grid>
            ))}
          </Grid>
        )}

        {/* ================= OPERATIONAL NOTES ================= */}
        {insights.operationalNotes?.length > 0 && (
          <VuiBox mb={2}>
            {renderGlassCard({
              children: (
                <>
                  <VuiBox display="flex" alignItems="center" gap={1} mb={1}>
                    {getIcon("IoSparkles", { color: "#f59e0b", size: 15 })}

                    <VuiTypography variant="button" fontWeight="bold">
                      Operational Notes
                    </VuiTypography>
                  </VuiBox>

                  <Stack spacing={0.7}>
                    {insights.operationalNotes.map((note, i) => (
                      <VuiTypography key={i} variant="caption">
                        • {note}
                      </VuiTypography>
                    ))}
                  </Stack>
                </>
              ),
            })}
          </VuiBox>
        )}

        {/* ================= INSIGHT CARDS ================= */}
        <Grid container spacing={2}>
          {insights.insightCards?.map((card, index) => (
            <Grid item xs={12} md={6} xl={4} key={index}>
              {renderGlassCard({
                p: 2,
                children: (
                  <>
                    {/*  Header */}
                    <VuiBox display="flex" alignItems="center" gap={1.2} mb={1}>
                      <VuiBox
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        sx={{
                          height: 20,
                          width: 20,
                          lineHeight: 0,
                        }}
                      >
                        {getIcon(card.icon, { context: card, size: 14 })}
                      </VuiBox>

                      <VuiTypography
                        variant="button"
                        fontWeight="bold"
                        sx={{
                          lineHeight: 1,
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        {card.title}
                      </VuiTypography>
                    </VuiBox>

                    {/* Description */}
                    {card.description && (
                      <VuiTypography variant="caption" display="block" mb={2}>
                        {card.description}
                      </VuiTypography>
                    )}

                    {/* Items */}
                    <Stack spacing={1.5}>
                      {card.items?.map((item, idx) => (
                        <VuiBox
                          key={idx}
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                        >
                          {/*  Label + sub */}
                          <VuiBox display="flex" flexDirection="column" sx={{ minWidth: 0 }}>
                            <VuiTypography
                              variant="caption"
                              sx={{
                                lineHeight: 1.2,
                                mb: item.sub ? 0.4 : 0, // add spacing if sub exists
                                display: "block", // 🔥 forces new line
                              }}
                            >
                              {item.label}
                            </VuiTypography>

                            {item.sub && (
                              <VuiTypography
                                variant="caption"
                                sx={{
                                  lineHeight: 1.1,
                                  opacity: 0.7,
                                  display: "block", // 🔥 forces new line
                                }}
                              >
                                {item.sub}
                              </VuiTypography>
                            )}
                          </VuiBox>

                          {/* Percentage value */}
                          <VuiTypography
                            variant="caption"
                            fontWeight="bold"
                            sx={{
                              minWidth: 40,
                              textAlign: "right",
                            }}
                          >
                            {item.value}
                          </VuiTypography>
                        </VuiBox>
                      ))}
                    </Stack>
                  </>
                ),
              })}
            </Grid>
          ))}
        </Grid>
      </VuiBox>
    </Card>
  );
}

export default GuestLifestyleInsights;
